import { useState, useCallback } from 'react';
import { adminApi } from '../../api/adminApi';
import { downloadJson } from '../../utils/downloadJson';
import { useLogger, WebComponentCode } from '@shared-ui/logging';

// Exports a test's QUESTIONS (by id) as a JSON bundle that re-imports through
// the existing Questions-page import wizard. The test (question-set) doc itself
// is NOT exported here — only the questions it references. This unblocks the
// dev → prod question migration without a dedicated question-set import.

export interface TestExportStatus {
  type: 'success' | 'error';
  message: string;
}

interface PendingExport {
  ids: string[];
  filenameBase: string;
}

export function useTestQuestionsExport() {
  const logger = useLogger(WebComponentCode.WEB_UI, 'test-questions-export');
  // When set, the embed-images choice dialog is shown for these question ids.
  const [pending, setPending] = useState<PendingExport | null>(null);
  const [isExporting, setIsExporting] = useState(false);
  const [exportStep, setExportStep] = useState('');
  const [status, setStatus] = useState<TestExportStatus | null>(null);
  const [warnings, setWarnings] = useState<string[] | null>(null);

  const clearStatus = useCallback(() => setStatus(null), []);
  const clearWarnings = useCallback(() => setWarnings(null), []);
  const cancelExport = useCallback(() => setPending(null), []);

  // Opens the embed-images choice dialog. `filenameBase` seeds the download name.
  const requestExport = useCallback((ids: string[], filenameBase: string) => {
    const unique = [...new Set(ids)];
    if (unique.length === 0) {
      setStatus({ type: 'error', message: 'This test has no questions to export' });
      return;
    }
    setPending({ ids: unique, filenameBase });
  }, []);

  const runExport = useCallback(async (embedImages: boolean) => {
    if (!pending) return;
    const req = pending;
    setPending(null);
    setIsExporting(true);
    setExportStep(embedImages ? 'Exporting with images…' : 'Exporting questions…');
    try {
      logger.info('Exporting test questions', { count: req.ids.length, embedImages });
      const result = await adminApi.exportQuestionsByIds(req.ids, embedImages);
      logger.info('Test questions export result', {
        questionCount: result.questions.length,
        hasImages: !!result.images,
        warningCount: result.warnings?.length || 0,
      });

      downloadJson(
        {
          questions: result.questions,
          ...(result.images ? { images: result.images } : {}),
          ...(result.warnings?.length ? { warnings: result.warnings } : {}),
        },
        `${req.filenameBase}-questions-${new Date().toISOString().split('T')[0]}.json`,
      );

      if (result.warnings?.length) setWarnings(result.warnings);
      const found = result.questions.length;
      const missing = req.ids.length - found;
      const imageMsg = result.images ? ` (with ${Object.keys(result.images).length} embedded images)` : '';
      const missingMsg = missing > 0 ? ` — ${missing} question(s) not found` : '';
      setStatus({ type: 'success', message: `Exported ${found} question(s)${imageMsg}${missingMsg}` });
      setTimeout(() => setStatus(null), 6000);
    } catch (err) {
      const error = err instanceof Error ? err : new Error(String(err));
      logger.error('Test questions export failed', error);
      setStatus({ type: 'error', message: `Export failed: ${error.message}` });
    } finally {
      setIsExporting(false);
      setExportStep('');
    }
  }, [pending, logger]);

  return {
    requestExport,
    runExport,
    cancelExport,
    isPickerOpen: pending !== null,
    isExporting,
    exportStep,
    status,
    clearStatus,
    warnings,
    clearWarnings,
  };
}
